"use client";

import { Box, Button, Input, PasswordInput, Stack, Text } from "@mantine/core";
import type {
  CodeChallengeMethod,
  LoginAuthenticationResponse,
  MedplumClient,
} from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { clearMedplumCookieAction } from "@/actions/auth/clear-medplum-cookie";
import { ProfileSelection } from "@/components/auth/login/ProfileSelection";
import { ScopeSelection } from "@/components/auth/login/ScopeSelection";
import { identifyProfile } from "@/lib/auth/identify-profile";
import { completeLoginFromCode, getSafeRedirectPath } from "@/lib/auth/login-flow";
import { persistCompletedLogin, resumeStoredLogin } from "@/lib/auth/persist-completed-login";
import { useLoginChallengeFlow } from "@/lib/auth/use-login-challenge-flow";
import { APP_BRAND_ALT_TEXT, APP_BRAND_ASSETS } from "@/lib/brand";
import { isMedplumAuthorizationError } from "@/lib/errors/is-medplum-authorization-error";
import { isMedplumResourceMissingError } from "@/lib/errors/is-medplum-resource-missing-error";
import useAsyncEffect from "@/lib/hooks/use-async-effect";
import { createLogger } from "@/lib/logger";
import { notifyError } from "@/lib/utils";
import styles from "./LoginBox.module.scss";

const logger = createLogger("login-box");
const START_LOGIN_ROUTE = "/api/auth/start-login";

interface LoginBoxProps {
  logoUrl?: string;
}

interface StartLoginResponseBody extends LoginAuthenticationResponse {
  membershipFilterToken?: string;
}

interface StartLoginRequestBody {
  email: string;
  password: string;
  codeChallenge: string;
  codeChallengeMethod: CodeChallengeMethod;
}

function isInvalidActiveLoginError(error: unknown): boolean {
  return isMedplumAuthorizationError(error) || isMedplumResourceMissingError(error);
}

async function clearInvalidActiveLogin({
  medplum,
  error,
}: {
  medplum: MedplumClient;
  error: unknown;
}) {
  await medplum.clear();
  const clearCookieResult = await clearMedplumCookieAction();
  if (!clearCookieResult?.success) {
    notifyError("Failed to clear invalid Medplum active login cookie", {
      error: new Error(clearCookieResult?.message ?? "Failed to clear Medplum cookie"),
      logger,
      notifyUser: false,
      context: {
        invalidActiveLoginErrorMessage: error instanceof Error ? error.message : undefined,
      },
    });
    return;
  }

  logger.warn("Cleared invalid Medplum active login", {
    error,
    errorMessage: error instanceof Error ? error.message : undefined,
  });
}

async function reportUnexpectedActiveLoginValidationError({
  medplum,
  error,
}: {
  medplum: MedplumClient;
  error: unknown;
}) {
  try {
    await medplum.clear();
  } catch (clearError) {
    logger.warn("Failed to clear unexpected Medplum active login", {
      error: clearError,
      errorMessage: clearError instanceof Error ? clearError.message : undefined,
    });
  }

  notifyError("Invalid medplum active login", {
    error: error instanceof Error ? error : undefined,
    logger,
    notifyUser: false,
  });
}

/**
 * Clears stale browser session state after a failed login attempt.
 */
async function clearFailedLoginSession(medplum: MedplumClient) {
  const activeLogin = medplum.getActiveLogin();
  if (activeLogin) {
    try {
      await medplum.signOut();
    } catch (error) {
      logger.warn("Failed to sign out after login error", {
        error,
        errorMessage: error instanceof Error ? error.message : undefined,
      });
    }
  }

  try {
    await medplum.clear();
  } catch (error) {
    logger.warn("Failed to clear Medplum session after login error", {
      error,
      errorMessage: error instanceof Error ? error.message : undefined,
    });
  }
}

/**
 * Renders the login box and handles multi-profile authentication.
 */
export function LoginBox({ logoUrl }: LoginBoxProps) {
  //------------------------------------------------------------------------------------------------
  //                                        DEPENDENCIES
  //------------------------------------------------------------------------------------------------
  const medplum = useMedplum();
  const router = useRouter();
  const searchParams = useSearchParams();

  //------------------------------------------------------------------------------------------------
  //                                           STATE
  //------------------------------------------------------------------------------------------------
  const [hasCheckedLogin, setHasCheckedLogin] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasHandledSwitchProfile, setHasHandledSwitchProfile] = useState(false);
  const nextUrl = searchParams.get("next");
  const isSwitchProfileRequested = useMemo(
    () => searchParams.get("switchProfile") === "true",
    [searchParams],
  );
  const emailChanged = useMemo(() => searchParams.get("emailChanged") === "true", [searchParams]);
  const emailChangePasswordResetRequired = useMemo(
    () => searchParams.get("emailChangePasswordResetRequired") === "true",
    [searchParams],
  );
  const emailChangeError = useMemo(() => searchParams.get("emailChangeError"), [searchParams]);
  const emailChangeStatusMessage = useMemo(() => {
    if (emailChanged) {
      return {
        type: "success" as const,
        text: emailChangePasswordResetRequired
          ? "Your email was updated. We've sent a new email to reset your password now."
          : "Your email was updated. Please log in with your new email address.",
      };
    }

    switch (emailChangeError) {
      case "expired-token":
        return {
          type: "error" as const,
          text: "This email verification link has expired. Please request a new email change.",
        };
      case "request-new-link":
        return {
          type: "error" as const,
          text: "This email verification link is no longer valid. Please request a new email change.",
        };
      case "invalid-token":
        return {
          type: "error" as const,
          text: "This email verification link is invalid.",
        };
      case "temporarily-unavailable":
        return {
          type: "error" as const,
          text: "We couldn't verify this email change link right now. Please try again in a few minutes.",
        };
      case "update-failed":
        return {
          type: "error" as const,
          text: "We couldn't complete your email change. Please request a new one.",
        };
      case "token-used":
        return {
          type: "error" as const,
          text: "This email verification link was already used. Please request a new email change.",
        };
      default:
        return undefined;
    }
  }, [emailChanged, emailChangeError, emailChangePasswordResetRequired]);

  //------------------------------------------------------------------------------------------------
  //                                        FUNCTIONS
  //------------------------------------------------------------------------------------------------
  const navigateAfterLogin = useCallback(() => {
    const path = getSafeRedirectPath({ nextPath: nextUrl });
    if (new URL(path, window.location.origin).pathname === "/api/fullscript/connect") {
      // OAuth requires a document navigation to follow the external redirect.
      window.location.assign(path);
      return;
    }
    router.push(path);
  }, [nextUrl, router]);

  /**
   * Completes login using an authorization code.
   */
  const completeLogin = useCallback(
    async ({ code }: { code: string }) => {
      const { profile, activeLogin } = await completeLoginFromCode({ medplum, code });
      identifyProfile(profile);
      await persistCompletedLogin(activeLogin);
      navigateAfterLogin();
    },
    [medplum, navigateAfterLogin],
  );

  const {
    errorMessage,
    loginStep,
    memberships,
    pendingScope,
    isSelectingProfile,
    isSelectingScope,
    setErrorMessage,
    handleLoginResponse,
    handleProfileSelection,
    handleScopeSelection,
    handleBackToLogin,
    handleBackToProfile,
    resetFlow,
  } = useLoginChallengeFlow({
    medplum,
    onCompleteLogin: completeLogin,
  });

  /**
   * Handles the login form submission.
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (!process.env.NEXT_PUBLIC_MEDPLUM_PROJECT_ID) {
        throw new Error("NEXT_PUBLIC_MEDPLUM_PROJECT_ID is not set");
      }

      const pkce = await medplum.startPkce();
      const requestBody: StartLoginRequestBody = {
        email,
        password,
        codeChallenge: pkce.codeChallenge,
        codeChallengeMethod: pkce.codeChallengeMethod,
      };
      const startLoginResponse = await fetch(START_LOGIN_ROUTE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const startLoginPayload = (await startLoginResponse.json()) as Partial<
        StartLoginResponseBody & { error?: string }
      >;
      if (!startLoginResponse.ok) {
        throw new Error(startLoginPayload.error || "Login failed. Please check your credentials.");
      }

      const loginAuthenticationResponse = startLoginPayload as LoginAuthenticationResponse;

      // NOTE: when filterSelectableMemberships (in use-login-challenge-flow) returns empty,
      // handleLoginResponse does not set an error. It renders an empty profile picker instead.
      // The telemetry for that case is emitted inside use-login-challenge-flow.ts (warn log +
      // Sentry captureMessage).
      await handleLoginResponse({ response: loginAuthenticationResponse });
    } catch (err) {
      logger.error("Error during login:", { error: err });
      setErrorMessage(err instanceof Error ? err.message : "An error occurred. Please try again.");

      // Sign out and clear the cache to prevent medplum from trying to reuse an invalid active
      // login from the local storage.
      await clearFailedLoginSession(medplum);
    } finally {
      setIsLoading(false);
    }
  };

  //------------------------------------------------------------------------------------------------
  //                                          EFFECTS
  //------------------------------------------------------------------------------------------------
  useAsyncEffect(async () => {
    if (hasCheckedLogin) return;

    try {
      if (isSwitchProfileRequested && !hasHandledSwitchProfile) {
        await clearMedplumCookieAction();
        try {
          await medplum.signOut();
        } catch (error) {
          logger.debug(
            `Error signing out during profile switch: ${JSON.stringify(error, null, 2)}`,
          );
        }
        await medplum.clear();
        resetFlow();
        setHasHandledSwitchProfile(true);
        router.replace("/login");
        setHasCheckedLogin(true);
        return;
      }

      const activeLogin = medplum.getActiveLogin();
      if (!activeLogin) return;

      const profile = activeLogin?.profile;
      if (!profile) throw new Error("No profile found in active login");

      // Make sure the profile is not pointing to a deleted resource, otherwise it will create the
      // following redirect loop:
      // 1. Here, it finds an active login, and redirects the user to the app.
      // 2. The app fails to create a medplum client with the given credentials because the user is gone.
      // 3. The app redirects the user back to the login page.
      // 4. The cycle repeats.
      const resource = await medplum.readReference(profile);
      if (!resource) throw new Error("Active login profile is not pointing to a valid resource");

      logger.debug(`Active login found, setting medplum cookie and redirecting to app`);
      if (!(await resumeStoredLogin({ medplum, activeLogin }))) {
        return;
      }
      navigateAfterLogin();
    } catch (error) {
      if (isInvalidActiveLoginError(error)) {
        await clearInvalidActiveLogin({ medplum, error });
        return;
      }

      await reportUnexpectedActiveLoginValidationError({ medplum, error });
    } finally {
      setHasCheckedLogin(true);
    }
  }, [
    hasCheckedLogin,
    hasHandledSwitchProfile,
    isSwitchProfileRequested,
    medplum,
    navigateAfterLogin,
    resetFlow,
  ]);

  const renderLoginStep = () => {
    if (loginStep === "form") {
      return (
        <form className={styles.formContent} onSubmit={handleSubmit}>
          <Stack className={styles.inputGroup} gap="xl">
            <Input
              aria-label="Email"
              placeholder="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              classNames={{
                input: styles.input,
              }}
            />
            <PasswordInput
              aria-label="Password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              classNames={{
                input: styles.input,
              }}
            />
            <div className={styles.buttonContainer}>
              <Link href={`/reset-password`} className={styles.forgotPasswordLink}>
                Forgot Password?
              </Link>
              <Button type="submit" variant="primary" loading={isLoading} className={styles.button}>
                {isLoading ? "Signing in..." : "Login"}
              </Button>
            </div>
          </Stack>
        </form>
      );
    }

    if (loginStep === "profile") {
      return (
        <ProfileSelection
          memberships={memberships}
          isSubmitting={isSelectingProfile}
          onSelect={(membership) => handleProfileSelection({ membership })}
          onBack={handleBackToLogin}
        />
      );
    }

    return (
      <ScopeSelection
        scope={pendingScope ?? ""}
        isSubmitting={isSelectingScope}
        onSubmit={handleScopeSelection}
        onBack={handleBackToProfile}
      />
    );
  };

  //------------------------------------------------------------------------------------------------
  //                                         RENDER
  //------------------------------------------------------------------------------------------------
  if (!hasCheckedLogin) return null;

  return (
    <div className={styles.grayRectangle}>
      <div className={styles.formWrapper}>
        <div className={styles.logoContainer}>
          <Image
            // Practice logos keep their own aspect ratio; only the fallback Ultralight
            // wordmark gets the fixed-slot + faded treatment (see LoginBox.module.scss).
            className={logoUrl ? styles.practiceLogo : styles.fallbackWordmark}
            src={logoUrl || APP_BRAND_ASSETS.greyWordmark}
            alt={logoUrl ? "Practice logo" : APP_BRAND_ALT_TEXT.wordmark}
            width={181}
            height={42}
            priority
          />
        </div>

        <div className={styles.loginOptionsContainer}>
          {emailChangeStatusMessage && (
            <Box
              className={
                emailChangeStatusMessage.type === "success"
                  ? styles.SuccessMessage
                  : styles.EmailChangeErrorMessage
              }
            >
              <Text size="sm">{emailChangeStatusMessage.text}</Text>
            </Box>
          )}

          {renderLoginStep()}

          {errorMessage && (
            <Box className={styles.ErrorMessage}>
              <Text size="sm">{errorMessage}</Text>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
}
